import { useEffect, useState } from "react";
import { Repeat, Send, X, Check, Loader2 } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, EmptyState, Pill } from "@/components/Brutal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useModuleGuard } from "@/lib/useModuleGuard";
import { useT } from "@/lib/i18n";
import { effectiveDagdeel } from "@/lib/labels";
import { Tip } from "@/components/Tip";
import api from "@/lib/api";
import { toast } from "sonner";

const OFFER_SECTIONS = [
  { key: "nu", label: "Nu" },
  { key: "ochtend", label: "Ochtend" },
  { key: "middag", label: "Middag" },
  { key: "avond", label: "Avond" },
];
const SCHEDULED = ["nu", "ochtend", "middag", "avond"];

export default function Ruilmarkt() {
  useModuleGuard("ruilmarkt");
  const { user } = useAuth();
  const tr = useT();
  const [swaps, setSwaps] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [respondTo, setRespondTo] = useState(null);
  const [message, setMessage] = useState("");
  const [offered, setOffered] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, u, t] = await Promise.all([api.get("/swaps"), api.get("/users"), api.get("/tasks")]);
    setSwaps(s.data); setUsers(u.data); setTasks(t.data);
  };
  useEffect(() => { load(); }, []);

  const nameOf = (id) => users.find((u) => u.user_id === id)?.name || "?";
  const taskTitle = (id) => tasks.find((t) => t.id === id)?.title || "taak";
  const myOpenTasks = tasks.filter((t) => t.status !== "klaar" && t.status !== "template" && t.id !== respondTo?.task_id);

  const offerPill = (t) => {
    const on = offered.includes(t.id);
    return (
      <button key={t.id} data-testid={`offer-task-${t.id}`} onClick={() => setOffered(on ? offered.filter((x) => x !== t.id) : [...offered, t.id])}
        className={`brutal-sm brutal-press rounded-full px-3 py-1.5 text-sm font-bold ${on ? "bg-brand-violet text-white" : "bg-white"}`}>{on && "✓ "}{t.title}</button>
    );
  };

  const submitResponse = async () => {
    setBusy(true);
    try {
      await api.post(`/swaps/${respondTo.id}/respond`, { message, offered_task_ids: offered });
      toast.success(tr("Reactie verstuurd!"));
      setRespondTo(null); setMessage(""); setOffered([]); load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
    finally { setBusy(false); }
  };

  const accept = async (swap, resp, chosenTaskId) => {
    try {
      await api.post(`/swaps/${swap.id}/accept`, { response_id: resp.id, chosen_task_id: chosenTaskId || null });
      toast.success(tr("Ruil akkoord! ✅"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
  };

  return (
    <div className="space-y-4" data-testid="ruilmarkt-page">
      <SectionTitle sub={tr("Vraag hulp uit of neem een taak over")}>{tr("Taakruil-markt")}</SectionTitle>
      {swaps.length === 0 && <EmptyState icon={Repeat} title={tr("Geen open verzoeken")} text={tr("Vraag een taak uit via de ruil-knop op een taak.")} />}
      {swaps.map((s) => {
        const mine = s.requester_id === user.user_id;
        const iResponded = s.responses?.some((r) => r.responder_id === user.user_id);
        return (
          <BrutalCard key={s.id} data-testid={`swap-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-head font-bold text-lg">{s.task_title}</h3>
                <p className="text-xs font-semibold text-brand-ink/50">{mine ? tr("Jouw verzoek") : `${tr("Van")} ${nameOf(s.requester_id)}`}{(() => {
                  const directed = (s.direct_to_ids && s.direct_to_ids.length) ? s.direct_to_ids : (s.direct_to ? [s.direct_to] : []);
                  const tg = s.target_group_ids || [];
                  if (directed.length) return ` · ${tr("aan")} ${directed.length} ${directed.length === 1 ? tr("persoon") : tr("Personen").toLowerCase()}`;
                  if (tg.length) return ` · ${tr("aan")} ${tg.length} ${tr("groep(en)")}`;
                  return ` · ${tr("aan iedereen")}`;
                })()}</p>
              </div>
              {mine && <Tip label={tr("Verzoek annuleren")}><button data-testid={`swap-cancel-${s.id}`} onClick={() => api.post(`/swaps/${s.id}/cancel`).then(load)} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><X className="w-4 h-4" strokeWidth={2.5} /></button></Tip>}
            </div>

            {mine ? (
              <div className="mt-3 space-y-2">
                {(s.responses || []).length === 0 && <p className="text-sm text-brand-ink/50">{tr("Nog geen reacties.")}</p>}
                {(s.responses || []).map((r) => (
                  <div key={r.id} data-testid={`response-${r.id}`} className="brutal-sm rounded-xl bg-brand-cream p-3">
                    <p className="font-bold text-sm">{r.responder_name} {tr("wil 'm overnemen")}</p>
                    {r.message && <p className="text-sm text-brand-ink/70">"{r.message}"</p>}
                    {r.offered_task_ids?.length > 0 ? (
                      <div className="mt-2">
                        <p className="text-xs font-bold text-brand-ink/60">{tr("In ruil doe jij één van:")}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {r.offered_task_ids.map((tid) => (
                            <button key={tid} data-testid={`accept-swap-${r.id}-${tid}`} onClick={() => accept(s, r, tid)}
                              className="brutal-sm brutal-press rounded-full px-3 py-1.5 text-xs font-bold bg-brand-green flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={2.5} />{taskTitle(tid)}</button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <BrutalButton data-testid={`accept-swap-${r.id}`} size="sm" variant="green" className="mt-2" onClick={() => accept(s, r)}><Check className="w-4 h-4" strokeWidth={2.5} />{tr("Accepteer")}</BrutalButton>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-3">
                {iResponded ? <Pill className="bg-brand-green/30">{tr("Je hebt gereageerd")}</Pill> : (
                  <BrutalButton data-testid={`respond-swap-${s.id}`} variant="blue" size="sm" onClick={() => { setRespondTo(s); setMessage(""); setOffered([]); }}>
                    <Repeat className="w-4 h-4" strokeWidth={2.5} />{tr("Reageren / ruilen")}
                  </BrutalButton>
                )}
              </div>
            )}
          </BrutalCard>
        );
      })}

      <Dialog open={!!respondTo} onOpenChange={(v) => !v && setRespondTo(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{tr("Reageren op")} "{respondTo?.task_title}"</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Neem de taak over of bied een ruil aan.</DialogDescription>
          <textarea data-testid="swap-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} placeholder={tr("Bericht (optioneel), bijv. 'Ik doe 'm wel!'")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <div className="brutal-sm rounded-xl bg-brand-green/15 px-3 py-2 text-sm font-semibold">{tr("Je kunt deze taak gewoon overnemen. Een taak terugruilen is optioneel.")}</div>
          <div>
            <p className="text-sm font-bold">{tr("Optioneel: bied een ruil aan")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{tr("Kies een taak uit het overzicht die de ander in ruil mag overnemen.")}</p>
            <div className="space-y-3">
              {myOpenTasks.length === 0 && <span className="text-sm text-brand-ink/40">{tr("Geen taken om aan te bieden — je kunt de taak zonder ruil overnemen.")}</span>}
              {OFFER_SECTIONS.map((sec) => {
                const list = myOpenTasks.filter((t) => effectiveDagdeel(t) === sec.key);
                if (!list.length) return null;
                return (
                  <div key={sec.key} data-testid={`offer-section-${sec.key}`}>
                    <p className="text-xs font-black text-brand-ink/45 uppercase tracking-wide mb-1.5">{tr(sec.label)}</p>
                    <div className="flex flex-wrap gap-2">{list.map((t) => offerPill(t))}</div>
                  </div>
                );
              })}
              {(() => {
                const rest = myOpenTasks.filter((t) => !SCHEDULED.includes(effectiveDagdeel(t)));
                if (!rest.length) return null;
                return (
                  <div data-testid="offer-section-none">
                    <p className="text-xs font-black text-brand-ink/45 uppercase tracking-wide mb-1.5">{tr("Niet ingedeeld")}</p>
                    <div className="flex flex-wrap gap-2">{rest.map((t) => offerPill(t))}</div>
                  </div>
                );
              })()}
            </div>
          </div>
          <BrutalButton data-testid="submit-response-btn" variant="green" className="w-full" disabled={busy} onClick={submitResponse}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={2.5} />}{offered.length > 0 ? tr("Ruilvoorstel versturen") : tr("Taak overnemen")}
          </BrutalButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
